import { ColorTypes, actions } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, ColumnSource, FilterSettings, RequestFilters, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { ColumnInfo, HistoryTable } from "@reporting/rp-types/server";

const PAGE_SIZE = 500;

/** Columns shown first: whether the row is archived, when and by whom it was invalidated */
const LEADING_COLUMNS = ['ARCHIVED', 'DATE_DELETED', 'WHO_DELETED_LOGIN', 'WHO_DELETED'];

/**
 * Columns the grid filters by the selected company at the start, those of the table that it has: the company and
 * its group (P_ACCOUNTING_GROUP calls the group ID_GROUP). The user may change or clear the filters.
 */
const COMPANY_FILTER_COLUMNS: Record<string, (a: number, table: HistoryTable) => number> = {
	ACCOUNTING: a => a,
	ID_ACCOUNTING_GROUP: (_, table) => table.id_accounting_group,
	ID_GROUP: (_, table) => table.id_accounting_group,
};

/**
 * Change history of one master or super table (/history): its invalidated rows and the rows of its `_LOG`
 * archive, optionally with the valid rows. Read only; long texts arrive cut (HistoryService::PREVIEW_LENGTH).
 */
export class HistoryGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _table: Promise<HistoryTable>;
	private _includeValid = false;

	constructor(
		/** company whose server and primary master database hold the table */
		private a: number,
		private tableName: string,
	) { super() }

	/** The table description; the options and the columns ask for it at once, so the request is shared */
	getTable(): Promise<HistoryTable> {
		this._table ??= rp.ms('server').get<HistoryTable>(`history/${this.a}/${this.tableName}`);
		return this._table;
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = this.orderColumns((await this.getTable())?.columns ?? []).map(col => this.createColumn(col));
		return this._columns;
	}

	private getLeadingColumns(columns: ColumnInfo[]): ColumnInfo[] {
		return LEADING_COLUMNS.map(name => columns.find(c => c.name === name)).filter(c => !!c);
	}

	/** Columns in the order of the grid: the leading ones, then the rest in the order of the table */
	private orderColumns(columns: ColumnInfo[]): ColumnInfo[] {
		const leading = this.getLeadingColumns(columns);
		return [...leading, ...columns.filter(c => !leading.includes(c))];
	}

	/** Exact match filters (`=<number>`) of the company columns the table has, by their position in the grid */
	private getCompanyFilters(table: HistoryTable): FilterSettings[] {
		if (!table) return [];
		return this.orderColumns(table.columns ?? [])
			.map((col, c) => ({ c, value: COMPANY_FILTER_COLUMNS[col.name]?.(this.a, table) }))
			.filter(f => f.value != null)
			.map(f => ({ c: f.c, term: `=${f.value}` }));
	}

	private createColumn(col: ColumnInfo): ColumnSource {
		const column: ColumnSource = {
			data: col.name,
			header: col.name,
			t: 't',
			e: false,
			w: 'auto',
		};
		const type = (col.type ?? '').toLowerCase();
		if (['tinyint', 'smallint', 'int', 'bigint'].includes(type)) {
			column.t = 'n';
			column.z = '0';
		} else if (['float', 'real', 'decimal', 'numeric', 'money', 'smallmoney'].includes(type)) {
			column.t = 'n';
		} else if (type === 'bit') {
			column.t = 'b';
		} else if (type === 'date') {
			column.t = 'd';
		} else if (['datetime', 'datetime2', 'smalldatetime', 'datetimeoffset'].includes(type)) {
			column.t = 'dt';
		} else if (col.length === -1 || ['text', 'ntext', 'xml'].includes(type)) {
			column.w = 300;
		}

		if (col.name === 'ARCHIVED') {
			column.header = msg('Archived');
			column.w = 90;
		} else if (col.name.endsWith('_LOGIN')) {
			column.header = `${col.name.slice(0, -'_LOGIN'.length)} (${msg('Login')})`;
		}
		return column;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const rows = await rp.ms('server').get<Record<string, unknown>[]>(`history/${this.a}/${this.tableName}/rows`,
			this.createRequestParams(filters, {
				valid: this._includeValid ? 1 : undefined,
				count: this.pageSize,
			}));
		return (rows ?? []).map(data => ({ data }));
	}

	onAction(action: string, cell: CellModel, event: actions.ActionEvent = null) {
		switch (action) {
			case 'includeValid':
				this._includeValid = !!event.action.value;
				this.editor.refresh();
				break;
		}
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		const table = await this.getTable();
		const columns = table?.columns ?? [];
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: { enabled: true, defaultSettings: this.getCompanyFilters(table) },
			pageSize: PAGE_SIZE,
			editable: false,
			freezeLeft: this.getLeadingColumns(columns).length,
			lightTheme: null,
			darkTheme: null,
			toolbar: {
				save: { enabled: false },
				discard: { enabled: false },
				new: { enabled: false },
			},
			customToolbar: [{
				type: 'togglebutton',
				id: 'includeValid',
				icon: { icon: 'eye' },
				text: msg('Including valid rows'),
				color: ColorTypes.Accent,
				value: this._includeValid,
			}],
		});
	}

}
